import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {CollaborationState} from '../../store/collaboration.reducer';
import {select, Store} from '@ngrx/store';
import {
  getConversationListInfo,
  getLowestModifiedDateForThisConversationList, showViewMoreInConversationList
} from '../../store/collaboration.selector';
import {
  CreateChatConversationAction,
  GetConversationChatAction,
  GetConversationListAction
} from '../../store/collaboration.action';
import {CollaborationConfig} from '../../store/collaboration.config';
import {CollaborationQuery} from '../../store/collaboration.query';
import {PlatformSidebarState} from '@shared/components/platform-sidebar/store/platform-sidebar.reducer';
import {SetCurrentRightSideComponentAction} from '@shared/components/platform-sidebar/store/platform-sidebar.action';
import {
  ChatPermissions,
  ConversationComponentName, ConversationListComponentName, EditConversationListComponentName,
  NewConversationComponentName
} from '../../store/collaboration.constants';
import {GraphqlContentScope} from '@shared/models/graphql-content-scope.model';
import {ContentScope} from '@shared/commands/command-base';
import {CallCardComponent, CallCardMessage} from '@features/calls/call-card/call-card.component';
import { MatDialog } from '@angular/material/dialog';
import {Observable, of, Subject} from 'rxjs';
import {catchError, debounceTime, distinctUntilChanged, map, switchMap, take, takeUntil} from 'rxjs/operators';
import {SecurityState} from '@shared/store/reducers/security.reducer';
import {getFeaturesInfo, getUserInfo, hasFeature} from '@shared/store/selectors/security.selector';
import {DialogService} from '@shared/services/dialog-service';
import {CreateChatConversationCommand} from "../../store/collaboration.command";
import {CollaborationHashtagService} from "../../store/collaboration-hashtag.service";
import {ConversationBaseComponent} from "../conversation-base/conversation-base.component";
import {SnackBarService} from "@core/services/snack-bar.service";
import {RightSidebarWaitScreenService} from "@core/services/right-sidebar-waitcreen.service";
import {ConversationListInfo} from "../../store/collaboration.models";
import {MessengerFlags} from '../../messenger/messenger-flags';
import {MessengerConversationOrganisationPort} from '../../messenger/conversation-organisation.port';
import {MessengerDrafts} from '../../messenger/messenger-drafts';
import {MemberStatus, MutedUntil} from '../../messenger/messenger.models';
import {callRowOf, conversationCallOf} from '../../messenger/call-log';
import {MessengerPresencePort} from '../../messenger/presence.port';
import {MESSAGE_TICKS, MessageTick, tickOf} from '../../messenger/message-ticks';
import {cardPreview, ChatLineTwo, emptyCopy, lineTwo, previewText, rowStamp} from '../../messenger/chat-list-format';
import {voiceWordsOf} from '../../messenger/voice-words';
import {orderChatList} from '../../messenger/chat-list-order';
import {chatFaceStyle} from '../../messenger/chat-face';
import {nextSegment} from '../../messenger/segment-keys';
import {ChatMember} from '../../messenger/chat-members';
import {NewChatGroup} from '../new-chat-sheet/new-chat-sheet.component';
import {ChatOverviewTab, MagicBarConversationService} from '../magic-bar-conversation/magic-bar-converstaion.service';
import {holdsMessages, OPEN_IN_MESSAGES, openInMessages} from '../../messenger/open-in-messages';

/** The list's filter (ITP-E-194-03): carbon's five-way `SegmentedControl` above the rows. */
export type ConversationListFilter = 'all' | 'unread' | 'mentions' | 'groups' | 'calls';

/** The row menu's mute lengths (ITP-E-194-04, B-06), as carbon and WhatsApp offer them. */
export type ConversationMuteLength = '8h' | '1w' | 'always';

/** A row's scope chip: the screen the conversation belongs to (a support ticket today; Mission or Project, B-04). */
export interface ConversationScopeChip {
  icon: string;
  label: string;
  title: string;
}

@Component({
    standalone: false,
    selector: 'conversation-list',
  templateUrl: './conversation-list.component.html',
  styleUrls: ['./conversation-list.component.scss']
})
export class ConversationListComponent extends ConversationBaseComponent implements OnInit {

  conversationList$: any;
  conversationList: any;
  hasFeature$: Observable<any>;
  showViewMoreInConversationList: boolean;
  lowestModifiedDateForThisConversationList: any = new Date(2200, 1, 1);
  @Input()  contextId: string;
  @Input() hideCreateConversationBar: boolean;
  /** The conversation the Messages page has open, marked in the list (ITP-E-194-01). Unused on the panel. */
  @Input() selectedConversationId: string;
  /** A conversation was picked on the Messages page (ITP-E-194-01); the page puts its id in the route. */
  @Output() conversationSelected = new EventEmitter<any>();

  currentConversationContextId: string;
  defaultContextIdForThisComponent = '[COMPANY_ID]-chat';

  /**
   * The list's preview of a call row (D-18, ITP-E-189-11): the same words the chat's card shows this viewer — "You started
   * a call" while live, "Voice call · mm:ss", "Missed voice call" or "No one answered" once over — built from the query's
   * per-viewer fields, never from the stored text.
   */
  callPreview(message: CallCardMessage): string {
    return CallCardComponent.view(message, undefined, {phase: 'idle'}).text;
  }

  /**
   * The call a row's second line shows: its last message when that is a call row, today's look; and in the Calls filter,
   * a row whose last message is not a call shows its last stored call (B-03, `lastCall`) through the same call line and
   * `callPreview`. Null for any other row.
   */
  callOf(conversation: any): CallCardMessage | null {
    const last = conversation?.lastMessage;
    if (last?.callInvitationId) {
      return last;
    }
    return this.filter === 'calls' ? this.lastCalls[conversation?.id] || null : null;
  }

  /**
   * B-03 (ITP-E-208-05): the conversation has a call the reader took part in, as the list item says (`hasCall`, `lastCall`),
   * or as a call row that arrived since the list loaded.
   */
  hasCall(conversation: any): boolean {
    return !!conversation?.hasCall || !!conversation?.lastCall?.invitationId || !!conversation?.lastMessage?.callInvitationId;
  }
  contextTitle: string;

  // FIND AND FILTER (ITP-E-194-03), in the panel and on the Messages page alike (D-03).
  /** What the search box holds. Today it filters the loaded rows by title; with B-01 on, the port's search does. */
  searchText = '';
  filter: ConversationListFilter = 'all';
  readonly filters: { id: ConversationListFilter, label: string }[];
  /** The rows the search and the filter let through. */
  visibleConversations: any[] = [];
  /** Conversation ids the port's search found (B-01), or null while the loaded rows are filtered by title. */
  private searchHits: Set<string> | null = null;
  /** B-02: conversations where somebody mentioned the reader and it is unread. */
  mentions: Record<string, boolean> = {};
  /** The scope chip per conversation: a support ticket from `sourceType` / `sourceInfo`, and B-04's Mission / Project. */
  scopeChips: Record<string, ConversationScopeChip> = {};
  private flaggedScopes: Record<string, ConversationScopeChip> = {};
  /** An unsent draft per conversation, as one line of text for the row's "Draft:" mark. */
  draftTexts: Record<string, string> = {};

  // ORGANISE (ITP-E-194-04): pin, mute, archive and mark as unread, each read and written through the conversation
  // organisation port and shown only while its flag is on (B-05..B-08). With all four off the row menu is today's.
  readonly canPin: boolean;
  readonly canMute: boolean;
  readonly canArchive: boolean;
  readonly canMarkUnread: boolean;
  readonly muteLengths: { id: ConversationMuteLength, label: string }[] = [
    {id: '8h', label: 'Mute for 8 hours'},
    {id: '1w', label: 'Mute for 1 week'},
    {id: 'always', label: 'Mute always'}
  ];
  /**
   * OPEN IN MESSAGES (ITP-E-268-02): the row ⋯ starts with it for a reader who holds the Messages page (OM-Messages), as
   * the thread ⋯ does (ITP-E-268-01), through the same helper.
   */
  readonly OPEN_IN_MESSAGES = OPEN_IN_MESSAGES;
  holdsMessages = false;
  pinned: Record<string, boolean> = {};
  private mutedUntil: Record<string, MutedUntil> = {};
  archived: Record<string, boolean> = {};
  markedUnread: Record<string, boolean> = {};
  /** The Archived view, opened from the list header: only archived rows, which the main list leaves out. */
  showArchived = false;
  /** How many of the loaded conversations are archived, for the header's way into the Archived view. */
  archivedCount = 0;
  private readonly searchText$ = new Subject<string>();
  private readonly conversationIds$ = new Subject<string[]>();

  // PRESENCE (ITP-E-194-09, B-15): a direct conversation's face carries the other person's dot, read through the
  // presence port while the flag is on. A group has no dot: a set of people has no presence (carbon). Off, today's faces.
  readonly showPresence: boolean;
  /** The status per face, keyed by lower-case email. */
  presence: Record<string, MemberStatus> = {};
  private readonly faceEmails$ = new Subject<string[]>();

  // CARBON'S ROWS (ITP-E-203-01). A face is 44px; a group's is two of its members as initials discs at 68% of that, the
  // lower one ringed in the panel's ground so the overlap reads as two discs.
  readonly faceStyle = chatFaceStyle(44);
  readonly groupFaceStyle = chatFaceStyle(30);
  readonly groupFaceRingStyle = chatFaceStyle(30, true);
  /** The New chat sheet is raised over the list (ITP-E-203-04). */
  newChatOpen = false;
  readonly overviewTabs: { id: ChatOverviewTab, label: string }[] = [
    {id: 'conversations', label: 'Conversations'},
    {id: 'members', label: 'Members'}
  ];
  /** Up to two members (not the reader, not gone) whose faces stand for a group, per conversation. */
  private groupFaces: Record<string, { email: string, name: string }[]> = {};
  /** The last message as one plain line, per conversation: the row truncates it and cannot carry its markup. */
  private previews: Record<string, string> = {};
  /** Each conversation's last stored call (B-03) as a call row reads it, worked out once per list. */
  private lastCalls: Record<string, CallCardMessage> = {};

  constructor(public collaborationStore: Store<CollaborationState>,
              public dialog: MatDialog,
              public dialogService: DialogService,
              public securityStore: Store<SecurityState>,
              public rightSidebarWaitScreenService: RightSidebarWaitScreenService,
              private platformSidebarStore: Store<PlatformSidebarState>,
              public snackbarService: SnackBarService,
              private collaborationHashtagService: CollaborationHashtagService,
              private messengerFlags: MessengerFlags,
              private organisation: MessengerConversationOrganisationPort,
              private messengerDrafts: MessengerDrafts,
              private presencePort: MessengerPresencePort,
              private magicBarConversationService: MagicBarConversationService,
  ) {
    super(collaborationStore, snackbarService, dialog, dialogService, securityStore, rightSidebarWaitScreenService);
    this.showViewMoreInConversationList = false;
    this.lowestModifiedDateForThisConversationList = new Date(2200, 3, 1);
    this.contextId = '[COMPANY_ID]-chat';
    this.contextTitle = 'Conversations';
    this.conversationComponentName = ConversationComponentName;
    this.hideCreateConversationBar = false;
    this.currentConversationContextId = null;
    this.filters = [
      {id: 'all', label: 'All'},
      {id: 'unread', label: 'Unread'},
      ...(this.messengerFlags.isOn('B-02') ? [{id: 'mentions' as ConversationListFilter, label: 'Mentions'}] : []),
      {id: 'groups', label: 'Groups'},
      {id: 'calls', label: 'Calls'}
    ];
    this.canPin = this.messengerFlags.isOn('B-05');
    this.canMute = this.messengerFlags.isOn('B-06');
    this.canArchive = this.messengerFlags.isOn('B-07');
    this.canMarkUnread = this.messengerFlags.isOn('B-08');
    this.showPresence = this.messengerFlags.isOn('B-15');
  }

  ngOnInit() {
    this.subscribeToFindAndFilter();
    this.subscribeToConversationList();
    this.getConversationList();
    this.securityStore.pipe(select(getUserInfo)).pipe(takeUntil(this.unsubscribe$)).subscribe(currentUser => {
      this.currentUser = currentUser;
      if (this.currentUser?.companyId === 'orbitaxsupport') {
        this.hideCreateConversationBar = true;
      }
      // A group's faces leave the reader out, so they are worked out again once the reader is known.
      this.buildRowParts();
    });
    this.hasFeature$ = this.securityStore.pipe(select(hasFeature));
    this.hasFeature$.pipe(map(holdsMessages), distinctUntilChanged(), takeUntil(this.unsubscribe$))
      .subscribe(holds => this.holdsMessages = holds);
  }

  /** The list's own title: on the Messages page and a context's list. The panel's header already reads "Chat". */
  /** A context's list (a case, a project) names itself; the Messages page column has no title, as carbon's has none. */
  get showHeading(): boolean {
    return !this.isPageHost && this.contextId !== this.defaultContextIdForThisComponent;
  }

  /** Carbon's Conversations | Members overview: the Chat panel's company chat only, never the Messages page. */
  get showTabs(): boolean {
    return !this.isPageHost && this.contextId === this.defaultContextIdForThisComponent;
  }

  get overviewTab(): ChatOverviewTab {
    return this.magicBarConversationService.overviewTab();
  }

  setOverviewTab(tab: ChatOverviewTab) {
    this.magicBarConversationService.overviewTab.set(tab);
  }

  /**
   * A colleague picked in the Members tab: their 1:1 opens. `CreateChatConversationCommand` keys a 1:1 by its two
   * participants (`participants.ChecksumId()`), so an existing conversation reopens rather than a second one starting.
   * The roles are today's New conversation flow's: the reader as admin, the colleague as participant.
   */
  openDirectChat(member: ChatMember) {
    if (!this.currentUser || !member?.email) {
      return;
    }
    this.createChatConversation([
      {user: this.currentUser, permission: ChatPermissions[0]},
      {user: ConversationListComponent.userOf(member), permission: ChatPermissions[2]}
    ], member.name, false);
  }

  /** The New chat disc: on the Chat panel, for anybody who may start a chat. The Messages page has its own button. */
  get showNewChat(): boolean {
    return !this.hideCreateConversationBar && !this.isPageHost;
  }

  /**
   * The row's ⋯ holds Open in Messages (ITP-E-268-02) and what a flag has switched on (B-05..B-08); with none, the control
   * is not drawn.
   */
  get hasRowMenu(): boolean {
    return this.offersOpenInMessages || this.canPin || this.canMute || this.canArchive || this.canMarkUnread;
  }

  /**
   * Open in Messages is offered in the Chat panel's company list to a holder of OM-Messages (ITP-E-268-02): never in a
   * context's list, which the page does not list, nor on the Messages page itself.
   */
  get offersOpenInMessages(): boolean {
    return this.holdsMessages && this.showTabs;
  }

  /** A row opens in Messages when the list offers it, the row has an id and it is not a support-ticket chat. */
  canOpenInMessages(conversation: any): boolean {
    return this.offersOpenInMessages && !!conversation?.id
      && (conversation.sourceType || '').toLowerCase() !== 'orbitaxsupport';
  }

  /** The chat in a tab of its own on the Messages page; the panel stays where it was, and the chat is not opened here. */
  openInMessages(conversation: any) {
    openInMessages(conversation.id);
  }

  get emptyText(): string {
    return emptyCopy(this.filter, this.showArchived, this.isPageHost);
  }

  /** The page's segmented filter moves with the arrow keys, as carbon's SegmentedControl does. */
  onFilterKey(event: KeyboardEvent, index: number): void {
    const next = nextSegment(event.key, index, this.filters.length);
    if (next !== null) {
      event.preventDefault();
      this.onFilterChanged(this.filters[next].id);
    }
  }

  trackById(index: number, conversation: any): string {
    return conversation?.id;
  }

  /** The row's stamp: HH:mm today, dd/mm/yy before. */
  stampOf(conversation: any): string {
    return rowStamp(conversation?.lastMessage?.createdDate);
  }

  previewOf(conversation: any): string {
    return this.previews[conversation?.id] || '';
  }

  groupFacesOf(conversation: any): { email: string, name: string }[] {
    return this.groupFaces[conversation?.id] || [];
  }

  personName(conversation: any): string {
    const info = conversation?.titleAvatarInfo;
    return [info?.firstName, info?.lastName].filter(Boolean).join(' ');
  }

  /** What the row's second line leads with and the marks after it, in carbon's priority. */
  lineTwoOf(conversation: any): ChatLineTwo {
    const last = conversation?.lastMessage;
    const call = this.callOf(conversation);
    const isCall = !!call;
    return lineTwo({
      draft: this.draftOf(conversation),
      isCall,
      missedCall: !!call && CallCardComponent.view(call, undefined, {phase: 'idle'}).missed,
      tick: last && !isCall ? this.previewTick(last) : null,
      mention: !!this.mentions[conversation?.id],
      unreadCount: conversation?.unreadMessageCount || 0,
      markedUnread: !!this.markedUnread[conversation?.id],
      pinned: !!this.pinned[conversation?.id],
      muted: this.isMuted(conversation)
    }, {pin: this.canPin, mute: this.canMute});
  }

  /** The faces and the plain last line of each loaded conversation, worked out once per list rather than per redraw. */
  private buildRowParts() {
    const me = this.currentUser?.email?.toLowerCase();
    const faces: Record<string, { email: string, name: string }[]> = {};
    const previews: Record<string, string> = {};
    const lastCalls: Record<string, CallCardMessage> = {};
    (this.conversationList || []).forEach(conversation => {
      const lastCall = callRowOf(conversationCallOf(conversation.lastCall));
      if (lastCall) {
        lastCalls[conversation.id] = lastCall;
      }
      if (conversation.isGroupConversation) {
        faces[conversation.id] = (conversation.roles || [])
          .filter(role => !role.hasLeft && role.user?.email && role.user.email.toLowerCase() !== me)
          .slice(0, 2)
          .map(role => ({email: role.user.email, name: [role.user.firstName, role.user.lastName].filter(Boolean).join(' ')}));
      }
      const last = conversation.lastMessage;
      // A meeting card row reads as who started it, never "<Name>: <fallback>" (ITP-E-208-02).
      const card = cardPreview(last);
      if (card) {
        previews[conversation.id] = card;
      } else if (last && !last.callInvitationId) {
        // ITP-E-249-06 (B-25): a voice message has no text: it reads "Voice message (0:42)" by its mark, under the same rules.
        const text = voiceWordsOf(last) || previewText(last.message) || (last.attachment ? 'added an attachment.' : '');
        const who = conversation.isGroupConversation && !last.isSystemMessage && text
          ? (last.isMine ? 'You: ' : `${last.createdBy?.firstName || ''}: `) : '';
        previews[conversation.id] = who + text;
      }
    });
    this.groupFaces = faces;
    this.previews = previews;
    this.lastCalls = lastCalls;
  }
  getConversationList() {
    // The Messages page remounts between `/meet/messages` and `/meet/messages/<id>` (ITP-E-194-01): a list already in
    // the store stays on screen while it refreshes.
    if (!(this.isPageHost && this.conversationList?.length)) {
      this.showActionWaitScreen();
    }
    if (this.contextId === this.defaultContextIdForThisComponent) {
      this.collaborationStore.dispatch(new GetConversationListAction(CollaborationConfig.CollaborationApiKey,
        CollaborationQuery.getConversationList()));
    }
  }

  viewMoreConversation() {
    if (this.currentConversationContextId) {
      this.collaborationStore.dispatch(new GetConversationListAction(CollaborationConfig.CollaborationApiKey,
        CollaborationQuery.getConversationListByContextId(this.currentConversationContextId, new Date(this.lowestModifiedDateForThisConversationList), true), true));
    }
  }

  subscribeToConversationList() {
    this.conversationList$ = this.collaborationStore.select(getConversationListInfo).pipe(takeUntil(this.unsubscribe$));
    this.conversationList$.subscribe(conversationListInfo => {
      if (!conversationListInfo) {
        conversationListInfo = new ConversationListInfo();
      }

      this.conversationList = conversationListInfo.queryConversations;
      this.lowestModifiedDateForThisConversationList = conversationListInfo.lowestModifiedDate;
      this.showViewMoreInConversationList = conversationListInfo.hasMoreConversations;
      this.currentConversationContextId = conversationListInfo.contextId;
      this.contextTitle = conversationListInfo.contextTitle;
      this.hideActionWaitScreen();
      if (this.conversationList && this.conversationList.length) {
        this.conversationList.forEach(conversation => {
          this.currentChatConversation = conversation; // to avoid code duplication
          this.initDefaultRoles();
          conversation.manageParticipantsRoles = this.roles;
          conversation.maxPermission = this.maxPermission;
        });
      }
      this.scopeChips = this.buildScopeChips();
      this.buildRowParts();
      this.conversationIds$.next((this.conversationList || []).map(conversation => conversation.id));
      this.faceEmails$.next([...new Set<string>((this.conversationList || []).map(conversation => this.faceEmail(conversation))
        .filter(Boolean))]);
      this.refreshVisibleConversations();
    });
  }

  onSearchChanged(text: string) {
    this.searchText = text || '';
    this.searchHits = null;
    this.searchText$.next(this.searchText.trim());
    this.refreshVisibleConversations();
  }

  clearSearch() {
    this.onSearchChanged('');
  }

  onFilterChanged(filter: ConversationListFilter) {
    this.filter = filter;
    this.refreshVisibleConversations();
  }

  /** The draft mark, except on the conversation the Messages page has open: its composer shows the draft itself. */
  draftOf(conversation: any): string {
    return this.isPageHost && conversation.id === this.selectedConversationId ? '' : this.draftTexts[conversation.id] || '';
  }

  /**
   * The search, the flagged marks and the drafts. Search filters the loaded rows by title as the user types; with B-01
   * on, the port's search (`search` on the conversation list query) replaces it once it answers. Mentions (B-02) and the
   * Mission / Project chips (B-04) are read through the conversation organisation port while their flags are on.
   */
  private subscribeToFindAndFilter() {
    this.searchText$.pipe(
      debounceTime(250),
      distinctUntilChanged(),
      switchMap(text => !text || !this.messengerFlags.isOn('B-01') ? of(null) : this.organisation.search(text).pipe(
        map(hits => new Set(hits.map(hit => hit.conversationId))),
        catchError(() => of(null)))),
      takeUntil(this.unsubscribe$)
    ).subscribe(hits => {
      this.searchHits = hits;
      this.refreshVisibleConversations();
    });

    const ids$ = this.conversationIds$.pipe(
      distinctUntilChanged((a, b) => a.length === b.length && a.every((id, i) => id === b[i])));
    if (this.messengerFlags.isOn('B-02')) {
      ids$.pipe(
        switchMap(ids => this.organisation.unreadMentions(ids).pipe(catchError(() => of({} as Record<string, boolean>)))),
        takeUntil(this.unsubscribe$)
      ).subscribe(mentions => {
        this.mentions = mentions || {};
        this.refreshVisibleConversations();
      });
    }
    if (this.messengerFlags.isOn('B-04')) {
      ids$.pipe(
        switchMap(ids => this.organisation.scopes(ids).pipe(catchError(() => of({})))),
        takeUntil(this.unsubscribe$)
      ).subscribe(scopes => {
        this.flaggedScopes = {};
        Object.keys(scopes || {}).forEach(id => this.flaggedScopes[id] = {
          icon: scopes[id].kind === 'Mission' ? 'fa-solid fa-rocket' : 'fa-solid fa-folder',
          label: scopes[id].label,
          title: `${scopes[id].kind} ${scopes[id].key}`
        });
        this.scopeChips = this.buildScopeChips();
      });
    }

    // B-05..B-08: each read answers again whenever its marks change, so a row moves or re-marks as soon as it is set.
    if (this.canPin) {
      this.readMarks(ids$, ids => this.organisation.pinned(ids), marks => this.pinned = marks);
    }
    if (this.canMute) {
      this.readMarks(ids$, ids => this.organisation.mutedUntil(ids), marks => this.mutedUntil = marks);
    }
    if (this.canArchive) {
      this.readMarks(ids$, ids => this.organisation.archived(ids), marks => this.archived = marks);
    }
    if (this.canMarkUnread) {
      this.readMarks(ids$, ids => this.organisation.markedUnread(ids), marks => this.markedUnread = marks);
    }

    if (this.showPresence) {
      this.faceEmails$.pipe(
        distinctUntilChanged((a, b) => a.join() === b.join()),
        switchMap(emails => !emails.length ? of({} as Record<string, MemberStatus>) :
          this.presencePort.statuses(emails.map(email => ({email}))).pipe(catchError(() => of({} as Record<string, MemberStatus>)))),
        takeUntil(this.unsubscribe$)
      ).subscribe(statuses => this.presence = statuses || {});
    }

    this.messengerDrafts.drafts$.pipe(takeUntil(this.unsubscribe$)).subscribe(drafts => {
      const texts: Record<string, string> = {};
      Object.keys(drafts).forEach(id => texts[id] = MessengerDrafts.textOf(drafts[id]));
      this.draftTexts = texts;
    });
  }

  private readMarks<T>(ids$: Observable<string[]>, read: (ids: string[]) => Observable<Record<string, T>>,
                       apply: (marks: Record<string, T>) => void) {
    ids$.pipe(
      switchMap(ids => read(ids).pipe(catchError(() => of({} as Record<string, T>)))),
      takeUntil(this.unsubscribe$)
    ).subscribe(marks => {
      apply(marks || {});
      this.refreshVisibleConversations();
    });
  }

  private refreshVisibleConversations() {
    const query = this.searchText.trim().toLowerCase();
    const list = this.conversationList || [];
    this.archivedCount = list.filter(conversation => this.archived[conversation.id]).length;
    const visible = list.filter(conversation => {
      if (!!this.archived[conversation.id] !== this.showArchived) {
        return false;
      }
      if (!this.passesFilter(conversation)) {
        return false;
      }
      if (!query) {
        return true;
      }
      return this.searchHits ? this.searchHits.has(conversation.id) : (conversation.title || '').toLowerCase().includes(query);
    });
    // The list's one order (ITP-E-240-04): pinned rows first in the server's order (B-05), then the rest newest first by the
    // date each row shows, its latest message. `modifiedDate`, which a read stamps, orders nothing.
    this.visibleConversations = orderChatList(visible, conversation => this.canPin && !!this.pinned[conversation.id],
      conversation => !!this.archived[conversation.id]);
  }

  /** The person a direct conversation's face shows, by lower-case email; none for a group, a case or notes to self. */
  private faceEmail(conversation: any): string | null {
    const email = conversation?.titleAvatarInfo?.email?.toLowerCase();
    const me = this.currentUser?.email?.toLowerCase();
    return !conversation?.isGroupConversation && conversation?.sourceType !== 'DAC6' && email && email !== me ? email : null;
  }

  /** B-15: the dot on a row's face, or null for today's face. */
  faceStatus(conversation: any): MemberStatus | null {
    const email = this.showPresence ? this.faceEmail(conversation) : null;
    return email ? this.presence[email] || null : null;
  }

  /** B-06: the conversation is muted now: for always, or until a time still ahead. */
  isMuted(conversation: any): boolean {
    const until = this.mutedUntil[conversation?.id];
    return !!until && (until === 'always' || new Date(until).getTime() > Date.now());
  }

  /** The glyph and words of each delivery tick (ITP-E-194-08). */
  readonly messageTicks = MESSAGE_TICKS;

  /** The last line's tick: today's `readStatus` on the reader's own message. Delivered is the thread's, not the row's. */
  previewTick(message: any): MessageTick | null {
    return tickOf(message);
  }

  /** The row's unread look: unread messages, or the reader marked it unread (B-08). */
  looksUnread(conversation: any): boolean {
    return conversation.unreadMessageCount > 0 || !!this.markedUnread[conversation.id];
  }

  openArchived() {
    this.showArchived = true;
    this.refreshVisibleConversations();
  }

  closeArchived() {
    this.showArchived = false;
    this.refreshVisibleConversations();
  }

  togglePinned(conversation: any) {
    this.organise(this.organisation.setPinned(conversation.id, !this.pinned[conversation.id]),
      'The conversation could not be pinned.');
  }

  mute(conversation: any, length: ConversationMuteLength) {
    const hours = length === '8h' ? 8 : 7 * 24;
    const until: MutedUntil = length === 'always' ? 'always' : new Date(Date.now() + hours * 3600 * 1000).toISOString();
    this.organise(this.organisation.mute(conversation.id, until), 'The conversation could not be muted.');
  }

  unmute(conversation: any) {
    this.organise(this.organisation.mute(conversation.id, null), 'The conversation could not be unmuted.');
  }

  toggleArchived(conversation: any) {
    this.organise(this.organisation.setArchived(conversation.id, !this.archived[conversation.id]),
      'The conversation could not be archived.');
  }

  setMarkedUnread(conversation: any, unread: boolean) {
    this.organise(this.organisation.setMarkedUnread(conversation.id, unread), 'The conversation could not be marked.');
  }

  /** A write through the port. The reads answer with the new marks; a failure says so and changes nothing. */
  private organise(write: Observable<void>, failure: string) {
    write.pipe(take(1), takeUntil(this.unsubscribe$)).subscribe({error: () => this.snackbarService.showError(failure)});
  }

  private passesFilter(conversation: any): boolean {
    switch (this.filter) {
      case 'unread':
        return this.looksUnread(conversation);
      case 'mentions':
        return !!this.mentions[conversation.id];
      case 'groups':
        return !!conversation.isGroupConversation;
      case 'calls':
        // B-03 (ITP-E-208-05): every conversation with a stored call the reader took part in, not only one whose last
        // message is a call.
        return this.hasCall(conversation);
      default:
        return true;
    }
  }

  private buildScopeChips(): Record<string, ConversationScopeChip> {
    const chips: Record<string, ConversationScopeChip> = {...this.flaggedScopes};
    (this.conversationList || []).forEach(conversation => {
      if (conversation.sourceType === 'OrbitaxSupport') {
        const ticket = ConversationListComponent.sourceInfoValue(conversation, 'TicketDisplayId');
        chips[conversation.id] = {
          icon: 'fa-solid fa-headset',
          label: ticket || 'Support',
          title: ticket ? `Help & Support ticket ${ticket}` : 'Help & Support'
        };
      }
    });
    return chips;
  }

  /** One entry of a conversation's `sourceInfo`: the GraphQL list of `{Key, Value}`, or a plain dictionary. */
  private static sourceInfoValue(conversation: any, key: string): string {
    const dictionary = conversation?.sourceInfo?.sourceInfoDictionary;
    if (!dictionary) {
      return '';
    }
    const value = Array.isArray(dictionary) ? dictionary.find(entry => entry?.Key === key)?.Value : dictionary[key];
    return value === undefined || value === null ? '' : String(value);
  }

  /**
   * New chat (ITP-E-203-04): carbon's sheet, over this list, on the Chat panel (the disc) and on the Messages page (its
   * New conversation button). It replaces the contact picker and the chat settings dialog in chat; both stay for the
   * features that still use them.
   */
  navigateToNewConversation() {
    this.newChatOpen = true;
  }

  closeNewChat() {
    this.newChatOpen = false;
  }

  /** The sheet's one pick: the 1:1, reopened if it exists. */
  onNewChatDirect(member: ChatMember) {
    this.closeNewChat();
    this.openDirectChat(member);
  }

  /** The sheet's group: the reader as its admin, the people picked as participants, under the name confirmed. */
  onNewChatGroup(group: NewChatGroup) {
    if (!this.currentUser || !group?.members?.length) {
      return;
    }
    this.closeNewChat();
    this.createChatConversation([
      {user: this.currentUser, permission: ChatPermissions[0]},
      ...group.members.map(member => ({user: ConversationListComponent.userOf(member), permission: ChatPermissions[2]}))
    ], group.name, true);
  }

  /** A directory member as a conversation role's user. */
  private static userOf(member: ChatMember): any {
    return {
      userName: member.email, email: member.email, firstName: member.firstName, lastName: member.lastName,
      companyId: member.companyId
    };
  }

  navigateToEditConversations() {
    if (this.isPageHost) {
      return;
    }
    this.platformSidebarStore.dispatch(new SetCurrentRightSideComponentAction({
      component: EditConversationListComponentName,
      data: {},
      componentInfo: null
    }));
  }

  navigateToConversation(conversation) {
    // Opening a conversation reads it: its "Mark as unread" mark goes (B-08).
    if (this.markedUnread[conversation.id]) {
      this.setMarkedUnread(conversation, false);
    }
    // On the Messages page the chat loads in place and the page takes the selection (ITP-E-194-01): no sidebar wait
    // screen, no sidebar swap.
    this.showRightSidebarWaitScreen();
    this.collaborationStore.dispatch(new GetConversationChatAction(CollaborationConfig.CollaborationApiKey,
      CollaborationQuery.getChatConversationById(conversation.id, conversation.scope), this.landing(this.conversationComponentName)));
    if (this.isPageHost) {
      this.conversationSelected.emit(conversation);
    }

/*    this.platformSidebarStore.dispatch(new SetCurrentRightSideComponentAction({
      component: ConversationComponentName,
      data: {},
      componentInfo: null
    }));*/
  }

  onHashTagClick(hashTag: string) {
    if (hashTag === "DAC6") {
      this.hasFeature$.pipe(
        take(1),
        map(hasFeatureFn => hasFeatureFn('DAC6-Overview'))
      ).subscribe(hasFeature => {
        if (!hasFeature) {
          this.snackbarService.showError('You don\'t have permission to access this tool');
        } else {
          this.executeHashTagAction(hashTag);
        }
      });
      return;
    }
    this.executeHashTagAction(hashTag);
  }

  private executeHashTagAction(hashTag: string) {
    const roles = [];
    if (this.permissions && this.currentUser) {
      const permission = this.permissions.find(p => p.permissionType === 290);
      if (permission !== undefined) {
        const newRole = {
          'user': this.currentUser,
          'permission': permission,
          'isChecked': true,
          'isDirty': true
        };
        roles.push(newRole);
      }
    }
    this.collaborationHashtagService.takeActionOnHashtag(hashTag, roles);
  }
}
